import { Link, useNavigate } from 'react-router-dom';
import {
  BoutonPrincipal,
  Carte,
  Compteur,
  EnteteEcran,
  Pastille,
  Chargement,
  MessageErreur,
  LigneAlerte,
  MAX_ALERTES_TABLEAU_BORD,
  PiedAlertes,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { useDashboardAdministratif } from '../lib/useDashboardAdministratif';

export function DashboardAdministratifPage(): JSX.Element {
  const navigate = useNavigate();
  const { site } = usePerimetre();

  const { data, isLoading, error } = useDashboardAdministratif(site);

  const actionsHeader = (
    <BoutonPrincipal onClick={() => navigate('/adm-inscriptions')}>
      + Nouvelle inscription
    </BoutonPrincipal>
  );

  if (isLoading) return <Chargement libelle="Chargement du tableau de bord..." />;
  if (error || !data) return <div className="p-4 sm:p-6"><MessageErreur message="Impossible de charger le tableau de bord." /></div>;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Pôle administratif"
        sousTitre="Inscription, dossier apprenant, documents CERFA, gestion des ruptures"
        actions={actionsHeader}
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Dossiers en cours"
          valeur={data.compteurs.dossiersEnCours}
          detail={`${data.compteurs.dossiersIncomplets} incomplets`}
          ton="navy"
        />
        <Compteur
          libelle="Pièces à vérifier"
          valeur={data.compteurs.piecesAVerifier}
          detail="déposées par les candidats"
          ton="warm"
        />
        <Compteur
          libelle="Documents à générer"
          valeur={data.compteurs.documentsAGenerer}
          detail="CERFA, calendriers"
          ton="blue"
        />
        <Compteur
          libelle="Ruptures 2026"
          valeur={data.compteurs.rupturesAnnee}
          detail={`${data.compteurs.tauxRupture} % des contrats`}
          ton="red"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0">
          <Carte titre="À traiter aujourd'hui">
            {data.taches.map(tache => (
              <Tache
                key={tache.id}
                titre={tache.titre}
                sousTitre={tache.sousTitre}
                statut={tache.statut}
                ton={tache.ton}
                page={tache.page}
              />
            ))}
          </Carte>

          <Carte titre="Accès rapides" sansBordure>
            <div className="grid grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-2">
              {data.accesRapides.map(acces => (
                <AccesRapide
                  key={acces.id}
                  titre={acces.titre}
                  sousTitre={acces.sousTitre}
                  page={acces.page}
                />
              ))}
            </div>
          </Carte>
        </div>

        <div className="space-y-6">
          <Carte
            titre="Mes alertes"
            actions={
              <Link
                to="/alertes"
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                Module d'alertes
              </Link>
            }
          >
            {data.alertes.slice(0, MAX_ALERTES_TABLEAU_BORD).map(alerte => (
              <LigneAlerte
                key={alerte.id}
                titre={alerte.titre}
                detail={alerte.sousTitre}
                ton={alerte.ton}
              />
            ))}
            <PiedAlertes
              restantes={data.alertes.length - MAX_ALERTES_TABLEAU_BORD}
              onClick={() => navigate('/alertes')}
            />
          </Carte>


        </div>
      </div>
    </div>
  );
}

function Tache({
  titre,
  sousTitre,
  statut,
  ton,
  page,
}: {
  titre: string;
  sousTitre: string;
  statut: string;
  ton: Parameters<typeof Pastille>[0]['ton'];
  page: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer items-center gap-4 px-4 sm:px-6 py-4 transition hover:bg-gray-50"
      onClick={() => navigate(page)}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-codialis-navy">{titre}</div>
        <div className="text-[11px] text-codialis-gray">{sousTitre}</div>
      </div>
      <Pastille texte={statut} ton={ton} />
    </div>
  );
}

function AccesRapide({ titre, sousTitre, page }: { titre: string; sousTitre: string; page: string }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(page)}
      className="w-full rounded-xl bg-gray-50 p-4 text-left transition hover:bg-gray-100"
    >
      <div className="text-sm font-semibold text-codialis-navy">{titre}</div>
      <div className="mt-0.5 text-[11px] text-codialis-gray">{sousTitre}</div>
    </button>
  );
}

